import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createSimType,
  deleteSimType,
  listSimTypes,
  updateSimType,
} from "../../api/endpoints/simTypes.js";
import { filterBySearch } from "../services/entityFilters.js";

export function useSimTypes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listSimTypes();
      setItems(data);
    } catch (err) {
      setError(err.message ?? "Failed to load SIM types");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const visible = useMemo(
    () => filterBySearch(items, search, ["name", "dataPlan", "status"]),
    [items, search],
  );

  const openCreate = useCallback(() => {
    setEditing(null);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((simType) => {
    setEditing(simType);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditing(null);
  }, []);

  const saveSimType = useCallback(async (payload) => {
    setSaving(true);
    setError(null);
    try {
      if (editing) {
        const updated = await updateSimType(editing.id, payload);
        setItems((prev) => prev.map((row) => (row.id === editing.id ? updated : row)));
      } else {
        const created = await createSimType(payload);
        setItems((prev) => [...prev, created]);
      }
      closeModal();
    } catch (err) {
      setError(err.message ?? "Failed to save SIM type");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [editing, closeModal]);

  const removeSimType = useCallback(async (simTypeId) => {
    setSaving(true);
    setError(null);
    try {
      await deleteSimType(simTypeId);
      setItems((prev) => prev.filter((row) => row.id !== simTypeId));
    } catch (err) {
      setError(err.message ?? "Failed to delete SIM type");
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  return {
    items: visible,
    allItems: items,
    loading,
    saving,
    error,
    search,
    setSearch,
    modalOpen,
    editing,
    openCreate,
    openEdit,
    closeModal,
    saveSimType,
    removeSimType,
    reload,
    total: items.length,
  };
}
